import * as React from "react";
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { ORGANIZATION_MODULES } from "@/lib/constants";
import { NavItem } from "@/components/layout/NavItem";
import { OrgSwitcher } from "@/components/layout/OrgSwitcher";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useLanguage } from "@/contexts/LanguageContext";

const SIDEBAR_COLLAPSED_KEY = "atelier.sidebar-collapsed";

export function Sidebar() {
  const { t } = useLanguage();
  const [collapsed, setCollapsed] = React.useState(
    () => window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1"
  );

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? "1" : "0");
      return next;
    });
  };

  return (
    <TooltipProvider delayDuration={200}>
      <aside
        className={cn(
          "hidden h-screen shrink-0 flex-col border-r border-border bg-surface transition-[width] duration-200 lg:flex",
          collapsed ? "w-[68px]" : "w-64"
        )}
      >
        <div className="flex h-16 items-center gap-2 border-b border-border px-3">
          <div className="min-w-0 flex-1">
            <OrgSwitcher collapsed={collapsed} />
          </div>
        </div>

        <nav className="flex-1 space-y-0.5 overflow-y-auto px-2.5 py-4" aria-label="Primary">
          {ORGANIZATION_MODULES.map((module) =>
            collapsed ? (
              <Tooltip key={module.path}>
                <TooltipTrigger asChild>
                  <div>
                    <NavItem module={module} collapsed />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="right">{t(module.label)}</TooltipContent>
              </Tooltip>
            ) : (
              <NavItem key={module.path} module={module} />
            )
          )}
        </nav>

        <div className="border-t border-border p-2.5">
          <button
            onClick={toggleCollapsed}
            className="flex w-full items-center justify-center gap-2 rounded-md px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label={t(collapsed ? "Expand sidebar" : "Collapse sidebar")}
          >
            {collapsed ? (
              <PanelLeftOpen className="h-4 w-4" aria-hidden="true" />
            ) : (
              <>
                <PanelLeftClose className="h-4 w-4" aria-hidden="true" />
                <span>{t("Collapse")}</span>
              </>
            )}
          </button>
        </div>
      </aside>
    </TooltipProvider>
  );
}
